import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { LOGO } from "../data/site";
import { useContent } from "../i18n/content";

export const scrollToSection = (id, navigate, pathname) => {
    if (pathname !== "/") {
        navigate("/", { state: { scrollTo: id } });
        return;
    }
    const el = document.getElementById(id);
    if (!el) return;
    if (window.__lenis) {
        window.__lenis.scrollTo(el, { offset: -72, duration: 1.4 });
    } else {
        el.scrollIntoView({ behavior: "smooth" });
    }
};

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { ui, site, lang, setLang } = useContent();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        scrollToSection(id, navigate, pathname);
    };

    return (
        <header
            data-testid="main-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 border-b ${
                scrolled
                    ? "bg-[#292a24]/85 backdrop-blur-xl border-white/10"
                    : "bg-transparent border-transparent"
            }`}
        >
            <div className="max-w-[1440px] mx-auto px-6 lg:px-12 h-[72px] flex items-center justify-between gap-6">
                <button
                    data-testid="nav-logo"
                    onClick={() => go("home")}
                    className="flex items-center gap-3 shrink-0"
                    aria-label="Sadja Core Corp — Home"
                >
                    <img
                        src={LOGO}
                        alt="Sadja Core Corp logo"
                        className="logo-blend h-10 w-10 object-cover rounded-full"
                    />
                    <span className="font-display tracking-[0.18em] text-sm font-extrabold text-white">
                        SADJA <span className="text-[#f6c422]">CORE CORP</span>
                    </span>
                </button>

                <nav className="hidden xl:flex items-center gap-7" aria-label="Primary">
                    {site.navLinks.map((link) => (
                        <button
                            key={link.id}
                            data-testid={`nav-link-${link.id}`}
                            onClick={() => go(link.id)}
                            className="text-[11px] font-display font-semibold tracking-[0.18em] uppercase text-[#a2a59d] hover:text-[#f6c422] transition-colors duration-300"
                        >
                            {link.label}
                        </button>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <div className="hidden md:flex items-center border border-[#4a4c42]" role="group" aria-label="Language">
                        {["en", "id"].map((l) => (
                            <button
                                key={l}
                                data-testid={`lang-toggle-${l}`}
                                onClick={() => setLang(l)}
                                aria-pressed={lang === l}
                                className={`px-3 py-2 font-display text-[10px] font-bold tracking-[0.18em] uppercase transition-colors duration-300 ${
                                    lang === l
                                        ? "bg-[#f6c422] text-[#292a24]"
                                        : "text-[#a2a59d] hover:text-white"
                                }`}
                            >
                                {l}
                            </button>
                        ))}
                    </div>
                    <button
                        data-testid="nav-cta-explore-group"
                        onClick={() => go("ecosystem")}
                        className="hidden md:inline-flex items-center gap-2 bg-[#f6c422] text-[#292a24] font-display font-bold text-[11px] tracking-[0.16em] uppercase px-5 py-3 hover:bg-[#ffe169] transition-colors duration-300 group"
                    >
                        {ui.navCta}
                        <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </button>
                    <button
                        data-testid="nav-mobile-toggle"
                        onClick={() => setOpen((v) => !v)}
                        className="xl:hidden text-white p-2"
                        aria-label="Toggle navigation menu"
                        aria-expanded={open}
                    >
                        {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        data-testid="mobile-menu"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="xl:hidden overflow-hidden bg-[#1e1f1b]/95 backdrop-blur-xl border-t border-white/10"
                        aria-label="Mobile"
                    >
                        <div className="px-6 py-6 flex flex-col gap-1">
                            {site.navLinks.map((link, i) => (
                                <motion.button
                                    key={link.id}
                                    data-testid={`mobile-nav-link-${link.id}`}
                                    initial={{ opacity: 0, x: -16 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.05 * i }}
                                    onClick={() => go(link.id)}
                                    className="text-left font-display font-semibold tracking-[0.14em] uppercase text-sm text-white/80 hover:text-[#f6c422] py-3 border-b border-white/5 transition-colors"
                                >
                                    <span className="text-[#f6c422] mr-3 text-xs">{String(i + 1).padStart(2, "0")}</span>
                                    {link.label}
                                </motion.button>
                            ))}
                            <button
                                data-testid="mobile-nav-cta-explore-group"
                                onClick={() => go("ecosystem")}
                                className="mt-5 inline-flex items-center justify-center gap-2 bg-[#f6c422] text-[#292a24] font-display font-bold text-xs tracking-[0.16em] uppercase px-5 py-4"
                            >
                                {ui.navCta}
                                <ArrowUpRight className="w-4 h-4" />
                            </button>
                            <div className="mt-3 flex items-center border border-[#4a4c42]" role="group" aria-label="Language">
                                {["en", "id"].map((l) => (
                                    <button
                                        key={l}
                                        data-testid={`mobile-lang-toggle-${l}`}
                                        onClick={() => setLang(l)}
                                        aria-pressed={lang === l}
                                        className={`flex-1 px-3 py-3 font-display text-[11px] font-bold tracking-[0.18em] uppercase transition-colors duration-300 ${
                                            lang === l
                                                ? "bg-[#f6c422] text-[#292a24]"
                                                : "text-[#a2a59d] hover:text-white"
                                        }`}
                                    >
                                        {l === "en" ? "English" : "Bahasa Indonesia"}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};
